import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import type {
  ChecklistItemDTO,
  EntityName,
  ExpenseDTO,
  ItineraryItemDTO,
  PlaceDTO,
  SourceDTO,
  TripBundle,
  TripDTO,
  TripEvent,
  WidgetDTO,
} from "@shared/types";
import { stayNights, tripDays } from "@shared/types";
import { del, get, patch, post, put, readSSE } from "./api";
import { dayColor } from "./meta";
import { useUI } from "./store";

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

export type Bundle = TripBundle & { revision?: number };

const OFFLINE_KEY = (id: string) => `tb:bundle:${id}`;

function loadOffline(id: string): Bundle | undefined {
  try {
    const s = localStorage.getItem(OFFLINE_KEY(id));
    return s ? (JSON.parse(s) as Bundle) : undefined;
  } catch {
    return undefined;
  }
}

let saveTimer: number | undefined;
function saveOffline(id: string, b: Bundle) {
  clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(OFFLINE_KEY(id), JSON.stringify({ ...b, savedAt: new Date().toISOString() }));
    } catch {
      /* квота */
    }
  }, 800);
}

export function offlineSavedAt(id: string): string | null {
  try {
    const s = localStorage.getItem(OFFLINE_KEY(id));
    return s ? ((JSON.parse(s) as { savedAt?: string }).savedAt ?? null) : null;
  } catch {
    return null;
  }
}

export const tripKey = (id: string) => ["trip", id] as const;

export function useTripBundle(tripId: string) {
  const q = useQuery({
    queryKey: tripKey(tripId),
    queryFn: async () => {
      const started = Date.now() - 500;
      try {
        // события, пришедшие пока шёл запрос, могли оказаться свежее снимка — накладываем их сверху
        const b = replayEvents(tripId, await get<Bundle>(`/api/trips/${tripId}`), started);
        saveOffline(tripId, b);
        return b;
      } catch (e) {
        const off = loadOffline(tripId);
        if (off && (e as { status?: number }).status === 0) return off;
        throw e;
      }
    },
    placeholderData: () => loadOffline(tripId),
  });
  useEffect(() => {
    if (q.data) saveOffline(tripId, q.data);
  }, [q.data, tripId]);
  return q;
}

type Coll = "places" | "items" | "checklist" | "expenses" | "sources" | "widgets";
const ENTITY_COLL: Partial<Record<EntityName, Coll>> = {
  place: "places",
  item: "items",
  checklist: "checklist",
  expense: "expenses",
  source: "sources",
  widget: "widgets",
};

/** Применить изменение сущности к кешу */
/** Недавние realtime-события: переигрываются поверх снимка, который начали грузить до них */
const recentEvents = new Map<string, { ts: number; entity: EntityName; op: "upsert" | "delete"; id: string; data?: unknown }[]>();

function rememberEvent(tripId: string, entity: EntityName, op: "upsert" | "delete", id: string, data?: unknown) {
  const now = Date.now();
  const list = (recentEvents.get(tripId) ?? []).filter((e) => now - e.ts < 60_000);
  list.push({ ts: now, entity, op, id, data });
  recentEvents.set(tripId, list);
}

function replayEvents(tripId: string, b: Bundle, since: number): Bundle {
  let out = b;
  for (const e of recentEvents.get(tripId) ?? []) if (e.ts >= since) out = changeBundle(out, e.entity, e.op, e.id, e.data) ?? out;
  return out;
}

export function applyChange(qc: QueryClient, tripId: string, entity: EntityName, op: "upsert" | "delete", id: string, data?: unknown) {
  rememberEvent(tripId, entity, op, id, data);
  qc.setQueryData<Bundle>(tripKey(tripId), (b) => (b ? changeBundle(b, entity, op, id, data) : b));
}

function changeBundle(b: Bundle, entity: EntityName, op: "upsert" | "delete", id: string, data?: unknown): Bundle {
  {
    if (entity === "trip" && data) return { ...b, trip: { ...b.trip, ...(data as TripDTO), myRole: b.trip.myRole } };
    if (entity === "vote") {
      return { ...b, places: b.places.map((p) => (p.id === id ? { ...p, votes: data as PlaceDTO["votes"] } : p)) };
    }
    const coll = ENTITY_COLL[entity];
    if (!coll) return b;
    const arr = b[coll] as { id: string }[];
    let next: { id: string }[];
    if (op === "delete") next = arr.filter((x) => x.id !== id);
    else {
      const i = arr.findIndex((x) => x.id === id);
      const prev = i >= 0 ? (arr[i] as Record<string, unknown>) : {};
      // голоса и счётчик комментариев приходят отдельно — сохраняем их
      const merged = { ...prev, ...(data as object) } as { id: string };
      next = i >= 0 ? arr.map((x, k) => (k === i ? merged : x)) : [...arr, merged];
    }
    if (coll === "items") (next as ItineraryItemDTO[]).sort((a, z) => a.day.localeCompare(z.day) || a.position - z.position);
    return { ...b, [coll]: next };
  }
}

/** Подписка на realtime-события поездки */
export function useTripEvents(tripId: string, meId?: string | null) {
  const qc = useQueryClient();
  const me = useRef(meId);
  me.current = meId;
  useEffect(() => {
    const ctrl = new AbortController();
    let stopped = false;
    let delay = 1000;
    const run = async () => {
      while (!stopped) {
        try {
          await readSSE(
            `/api/trips/${tripId}/events`,
            { method: "GET" },
            (event, data) => {
              if (event === "hello") {
                delay = 1000;
                // после переподключения — догоняем пропущенное
                void qc.invalidateQueries({ queryKey: tripKey(tripId) });
                return;
              }
              if (event !== "message") return;
              const e = data as TripEvent;
              if (e.type === "change") {
                if (e.entity === "member") void qc.invalidateQueries({ queryKey: tripKey(tripId) });
                else if (e.entity === "comment") {
                  void qc.invalidateQueries({ queryKey: ["comments", tripId] });
                  void qc.invalidateQueries({ queryKey: tripKey(tripId) });
                } else applyChange(qc, tripId, e.entity, e.op, e.id, e.data);
              } else if (e.type === "reload") {
                void qc.invalidateQueries({ queryKey: tripKey(tripId) });
              } else if (e.type === "presence") {
                useUI.getState().set({ presence: e.userIds });
              } else if (e.type === "focus" && e.userId === me.current) {
                // «покажи на карте» из Claude через MCP
                const ui = useUI.getState();
                if (e.day) ui.selectDay(e.day);
                if (e.bounds) ui.focusMap({ bounds: e.bounds });
                else if (e.center) ui.focusMap({ center: e.center, zoom: e.zoom });
                if (window.innerWidth < 1024) ui.set({ mobileView: "map" });
              }
            },
            ctrl.signal,
          );
        } catch {
          /* переподключимся */
        }
        if (stopped) break;
        await new Promise((r) => setTimeout(r, delay));
        delay = Math.min(delay * 2, 30_000);
      }
    };
    void run();
    return () => {
      stopped = true;
      ctrl.abort();
    };
  }, [tripId, qc]);
}

// ---------- мутации ----------

const base = (tripId: string) => `/api/trips/${tripId}`;

export const tripApi = {
  createPlace: (tripId: string, v: Partial<PlaceDTO>) => post<PlaceDTO>(`${base(tripId)}/places`, v),
  updatePlace: (tripId: string, id: string, v: Partial<PlaceDTO>) => patch<PlaceDTO>(`${base(tripId)}/places/${id}`, v),
  deletePlace: (tripId: string, id: string) => del<{ activityId: number | null }>(`${base(tripId)}/places/${id}`),
  createItem: (tripId: string, v: Partial<ItineraryItemDTO>) => post<ItineraryItemDTO>(`${base(tripId)}/items`, v),
  updateItem: (tripId: string, id: string, v: Partial<ItineraryItemDTO>) => patch<ItineraryItemDTO>(`${base(tripId)}/items/${id}`, v),
  deleteItem: (tripId: string, id: string) => del<{ activityId: number | null }>(`${base(tripId)}/items/${id}`),
  reorder: (tripId: string, day: string, ids: string[]) => post(`${base(tripId)}/items/reorder`, { day, ids }),
  createCheck: (tripId: string, v: Partial<ChecklistItemDTO>) => post<ChecklistItemDTO>(`${base(tripId)}/checklist`, v),
  updateCheck: (tripId: string, id: string, v: Partial<ChecklistItemDTO>) => patch<ChecklistItemDTO>(`${base(tripId)}/checklist/${id}`, v),
  deleteCheck: (tripId: string, id: string) => del<{ activityId: number | null }>(`${base(tripId)}/checklist/${id}`),
  createExpense: (tripId: string, v: Partial<ExpenseDTO>) => post<ExpenseDTO>(`${base(tripId)}/expenses`, v),
  updateExpense: (tripId: string, id: string, v: Partial<ExpenseDTO>) => patch<ExpenseDTO>(`${base(tripId)}/expenses/${id}`, v),
  deleteExpense: (tripId: string, id: string) => del<{ activityId: number | null }>(`${base(tripId)}/expenses/${id}`),
  deleteWidget: (tripId: string, id: string) => del<{ activityId: number | null }>(`${base(tripId)}/widgets/${id}`),
  addSource: (tripId: string, v: { url?: string; text?: string; title?: string }) => post<SourceDTO & { duplicate?: boolean }>(`${base(tripId)}/sources`, v),
  retrySource: (tripId: string, id: string, text?: string) => post<SourceDTO>(`${base(tripId)}/sources/${id}/retry`, { text }),
  deleteSource: (tripId: string, id: string) => del(`${base(tripId)}/sources/${id}`),
  addExtracted: (tripId: string, id: string, indexes: number[], status: "idea" | "want" = "idea") =>
    post<{ added: string[] }>(`${base(tripId)}/sources/${id}/add-places`, { indexes, status }),
  vote: (tripId: string, placeId: string, value: -1 | 0 | 1) => put(`${base(tripId)}/places/${placeId}/vote`, { value }),
  undo: (tripId: string, v: { batchId?: string; activityId?: number }) => post<{ undone: number; conflicts: string[] }>(`${base(tripId)}/undo`, v),
  updateTrip: (tripId: string, v: Partial<TripDTO>) => patch<TripDTO>(`${base(tripId)}`, v),
};

// ---------- производные данные ----------

export interface DayInfo {
  day: string;
  index: number;
  color: string;
  items: ItineraryItemDTO[];
  /** где ночуем этой ночью */
  night: ItineraryItemDTO | null;
  base: string | null;
  modes: string[];
}

export function useDerived(b: Bundle | undefined) {
  return useMemo(() => {
    if (!b) return null;
    const days = tripDays(b.trip.startDate, b.trip.endDate);
    const places = new Map(b.places.map((p) => [p.id, p]));
    const byDay = new Map<string, ItineraryItemDTO[]>();
    for (const it of b.items) byDay.set(it.day, [...(byDay.get(it.day) ?? []), it]);
    const nightOf = new Map<string, ItineraryItemDTO>();
    for (const it of b.items) if (it.kind === "stay" && it.status !== "cancelled") for (const d of stayNights(it.day, it.nights)) nightOf.set(d, it);
    const info: DayInfo[] = days.map((d, i) => {
      const items = (byDay.get(d) ?? []).sort((a, z) => a.position - z.position);
      const night = nightOf.get(d) ?? null;
      const np = night?.placeId ? places.get(night.placeId) : null;
      const firstPlace = items.map((x) => (x.placeId ? places.get(x.placeId) : null)).find(Boolean);
      return {
        day: d,
        index: i,
        color: dayColor(i, days.length),
        items,
        night,
        base: np?.region ?? np?.name ?? firstPlace?.region ?? (items.some((x) => x.kind === "transport") ? "в пути" : null),
        modes: items.filter((x) => x.kind === "transport" && x.mode).map((x) => x.mode!) as string[],
      };
    });
    const dayIndex = new Map(info.map((d) => [d.day, d]));
    // в каких днях место стоит в плане
    const placeDays = new Map<string, string[]>();
    for (const it of b.items) if (it.placeId) placeDays.set(it.placeId, [...new Set([...(placeDays.get(it.placeId) ?? []), it.day])]);
    const outside = b.items.filter((x) => x.day < b.trip.startDate || x.day > b.trip.endDate);
    const members = new Map(b.members.map((m) => [m.id, m]));
    return { days: info, dayIndex, places, placeDays, outside, members };
  }, [b]);
}

export type Derived = NonNullable<ReturnType<typeof useDerived>>;

export function widgetById(b: Bundle | undefined, id: string | null): WidgetDTO | null {
  if (!b || !id) return null;
  return b.widgets.find((w) => w.id === id) ?? null;
}
